import { getAuriaIntent } from "../ai/domain/intents.js";

const VALID_INTENTS = new Set(["relax", "rest", "recenter"]);

function getRotationKey(uid, intentKey) {
  return `harmonauria_breathing_rotation:${uid || "anonymous"}:${intentKey}`;
}

function uniqueIds(ids = []) {
  return [...new Set(ids.filter((id) => id !== null && id !== undefined))];
}

function sameIds(left = [], right = []) {
  if (left.length !== right.length) return false;
  return left.every((id, index) => id === right[index]);
}

function readRotation(uid, intentKey) {
  try {
    const raw = localStorage.getItem(getRotationKey(uid, intentKey));
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function writeRotation(uid, intentKey, value) {
  try {
    localStorage.setItem(getRotationKey(uid, intentKey), JSON.stringify(value));
  } catch {
    /* Le parcours reste utilisable si le stockage local est indisponible. */
  }
}

function rotatePoolForUser(ids, uid, intentKey) {
  const pool = uniqueIds(ids);
  if (pool.length < 2) return pool;
  const seed = `${uid || "anonymous"}:${intentKey}`.split("").reduce((sum, char) => sum + char.charCodeAt(0), 0);
  const offset = seed % pool.length;
  return [...pool.slice(offset), ...pool.slice(0, offset)];
}

function buildRemainingPool(accessibleIds, state, uid, intentKey) {
  const pool = rotatePoolForUser(accessibleIds, uid, intentKey);
  const lastSoundId = pool.includes(state.lastSoundId) ? state.lastSoundId : null;
  const previousPool = uniqueIds(state.poolSoundIds || []).filter((id) => pool.includes(id));
  const poolChanged = !sameIds(previousPool, pool);
  let remainingSoundIds = uniqueIds(state.remainingSoundIds || []).filter((id) => pool.includes(id) && id !== lastSoundId);

  if (poolChanged) {
    const additions = pool.filter((id) => id !== lastSoundId && !remainingSoundIds.includes(id));
    remainingSoundIds = [...remainingSoundIds, ...additions];
  }

  if (!remainingSoundIds.length) remainingSoundIds = pool.filter((id) => id !== lastSoundId);
  if (!remainingSoundIds.length && pool.length) remainingSoundIds = [...pool];

  return { lastSoundId, poolSoundIds: pool, remainingSoundIds };
}

export function normalizeDailyIntent(value) {
  return VALID_INTENTS.has(value) ? value : "relax";
}

export function normalizeDailyClassicDuration(value, isPremium) {
  if (value === "30sec") return "30sec";
  if (value === "2min") return "2min";
  if (value === "5min") return isPremium ? "5min" : "2min";
  if (value === "10min") return isPremium ? "5min" : "2min";
  return "2min";
}

export function getAccessibleDailyBreathingIds({ intentKey, isPremium, premiumSoundIds = [] }) {
  const premiumIds = new Set(premiumSoundIds);
  return (getAuriaIntent(normalizeDailyIntent(intentKey))?.breathingIds || []).filter((id) => isPremium || !premiumIds.has(id));
}

export function pickNextDailyBreathingId(uid, intentKey, accessibleIds = []) {
  const pool = uniqueIds(accessibleIds);
  if (!pool.length) return null;
  const state = buildRemainingPool(pool, readRotation(uid, intentKey), uid, intentKey);
  const nextId = state.remainingSoundIds[0] ?? pool[0];
  writeRotation(uid, intentKey, { lastSoundId: nextId, poolSoundIds: state.poolSoundIds, remainingSoundIds: state.remainingSoundIds.slice(1) });
  return nextId;
}

export function rememberDailyBreathingSelection(uid, intentKey, accessibleIds = [], soundId) {
  const pool = rotatePoolForUser(accessibleIds, uid, intentKey);
  if (!pool.includes(soundId)) return;
  writeRotation(uid, intentKey, { lastSoundId: soundId, poolSoundIds: pool, remainingSoundIds: pool.filter((id) => id !== soundId) });
}
